import { type MutableRefObject, useCallback } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { combinedSelectionCanvasBounds } from '../helpers/selection-canvas-bounds'
import { scheduleViewportScroll } from '../helpers/viewport-scroll'
import {
  computeFitWidthScale,
  computeToolbarZoomStep,
  computeZoomToRectScale,
} from '../helpers/zoom-scale'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/** Axis-aligned rect in canvas (document) space. */
type CanvasRect = { x: number; y: number; width: number; height: number }

/**
 * Viewport zoom/scroll commands exposed through the imperative handle and the marquee zoom: zoom
 * to a rect, center on a rect / the selection / the spread, fit width, zoom to selection, and the
 * toolbar zoom in/out steps.
 */
export function useViewportZoomCommands({
  scrollRef,
  pasteboardRef,
  internalShapeManagerRef,
  zoomRef,
  scaleRef,
  applyZoomToCanvasRectRef,
  onZoomChange,
  getCanvasOrigin,
  syncRulerScrollTransforms,
  fitContentDimensions,
  bleedWidth,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
  pasteboardRef: MutableRefObject<HTMLDivElement | null>
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  zoomRef: MutableRefObject<number>
  scaleRef: MutableRefObject<number>
  applyZoomToCanvasRectRef: MutableRefObject<((b: CanvasRect) => void) | null>
  onZoomChange: CenterCanvasConfig['onZoomChange']
  getCanvasOrigin: () => { originX: number; originY: number }
  syncRulerScrollTransforms: () => void
  fitContentDimensions: { width: number; height: number }
  bleedWidth: number
}) {
  /** Zoom and scroll so an axis-aligned canvas-space rect fills the viewport (with padding). */
  const applyZoomToCanvasRect = useCallback(
    (b: CanvasRect) => {
      const scroller = scrollRef.current

      if (!scroller || b.width <= 0 || b.height <= 0) return

      const nextScale = computeZoomToRectScale(scroller.clientWidth, scroller.clientHeight, b)

      onZoomChange?.(+nextScale.toFixed(3))

      const cx = b.x + b.width / 2
      const cy = b.y + b.height / 2

      scheduleViewportScroll(() => {
        const sc = scrollRef.current

        if (!sc) return null

        const { originX, originY } = getCanvasOrigin()
        const vcx = sc.clientWidth / 2
        const vcy = sc.clientHeight / 2

        return {
          scroller: sc,
          left: originX + cx * nextScale - vcx,
          top: originY + cy * nextScale - vcy,
        }
      }, syncRulerScrollTransforms)
    },
    [getCanvasOrigin, onZoomChange, syncRulerScrollTransforms, scrollRef]
  )

  applyZoomToCanvasRectRef.current = applyZoomToCanvasRect

  /** Pan (no zoom change) to center the viewport on a canvas-space rect, at the current scale. */
  const centerViewportOnCanvasRect = useCallback(
    (b: CanvasRect) => {
      const scroller = scrollRef.current

      if (!scroller || b.width <= 0 || b.height <= 0) return

      const cx = b.x + b.width / 2
      const cy = b.y + b.height / 2

      scheduleViewportScroll(() => {
        const sc = scrollRef.current

        if (!sc) return null

        const currentScale = zoomRef.current === 0 ? scaleRef.current : zoomRef.current
        const { originX, originY } = getCanvasOrigin()
        const vcx = sc.clientWidth / 2
        const vcy = sc.clientHeight / 2

        return {
          scroller: sc,
          left: originX + cx * currentScale - vcx,
          top: originY + cy * currentScale - vcy,
        }
      }, syncRulerScrollTransforms)
    },
    [getCanvasOrigin, syncRulerScrollTransforms, scrollRef, zoomRef, scaleRef]
  )

  /** Canvas-space bounds of the current selection, or the whole spread when nothing is selected. */
  const selectionOrSpreadCanvasBounds = useCallback(() => {
    const shapeMgr = internalShapeManagerRef.current
    const selection = combinedSelectionCanvasBounds(shapeMgr)

    if (selection && selection.width > 0 && selection.height > 0) return selection

    return { x: 0, y: 0, width: fitContentDimensions.width, height: fitContentDimensions.height }
  }, [fitContentDimensions, internalShapeManagerRef])

  /**
   * Recenter the viewport (no zoom change) on the current selection, or on the whole spread
   * when nothing is selected. Used after an explicit zoom percent commit and by the toolbar
   * zoom in/out buttons, so the zoomed-in/out area is always the selection (or spread).
   */
  const centerViewportOnSelectionOrSpread = useCallback(() => {
    centerViewportOnCanvasRect(selectionOrSpreadCanvasBounds())
  }, [centerViewportOnCanvasRect, selectionOrSpreadCanvasBounds])

  const fitWidth = useCallback(() => {
    const scroller = scrollRef.current

    if (!scroller || bleedWidth <= 0) return

    const nextScale = computeFitWidthScale(scroller.clientWidth, bleedWidth)

    onZoomChange?.(+nextScale.toFixed(3))

    scheduleViewportScroll(() => {
      const sc = scrollRef.current
      const board = pasteboardRef.current

      if (!sc || !board) return null

      const centerX = Math.max(0, (board.scrollWidth - sc.clientWidth) / 2)
      const centerY = Math.max(0, (board.scrollHeight - sc.clientHeight) / 2)

      return { scroller: sc, left: centerX, top: centerY }
    }, syncRulerScrollTransforms)
  }, [bleedWidth, onZoomChange, syncRulerScrollTransforms, scrollRef, pasteboardRef])

  const zoomToFitSelection = useCallback(() => {
    const shapeMgr = internalShapeManagerRef.current
    const b = combinedSelectionCanvasBounds(shapeMgr)

    if (!b || b.width <= 0 || b.height <= 0) return

    applyZoomToCanvasRect(b)
  }, [applyZoomToCanvasRect, internalShapeManagerRef])

  const zoomInFromToolbar = useCallback(() => {
    if (zoomRef.current === 0) return false

    const next = computeToolbarZoomStep(zoomRef.current, 'in')

    if (next == null) return false

    onZoomChange?.(next)
    centerViewportOnSelectionOrSpread()

    return true
  }, [onZoomChange, centerViewportOnSelectionOrSpread, zoomRef])

  const zoomOutFromToolbar = useCallback(() => {
    if (zoomRef.current === 0) return false

    const next = computeToolbarZoomStep(zoomRef.current, 'out')

    if (next == null) return false

    onZoomChange?.(next)
    centerViewportOnSelectionOrSpread()

    return true
  }, [onZoomChange, centerViewportOnSelectionOrSpread, zoomRef])

  return {
    centerViewportOnSelectionOrSpread,
    fitWidth,
    zoomToFitSelection,
    zoomInFromToolbar,
    zoomOutFromToolbar,
  }
}
